'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { createClient } from '@/utils/supabase/client';
import { routing } from '@/i18n/routing';
import OnboardingTour from './OnboardingTour';
import GuideModeToggleButton from './GuideModeToggleButton';
import { getOnboardingPath } from '@/lib/services/onboardingService';

const TOUR_SEEN_KEY = 'aifit_tour_seen';
const LANDING_VISITOR_GOAL = 'landing_visitor';
const DEFAULT_PATIENT_GOAL = 'patient_follow_up';

export default function GlobalOnboardingEntry({ defaultPatientGoal = DEFAULT_PATIENT_GOAL, labels }) {
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations('OnboardingTour');
  const supabase = useMemo(() => createClient(), []);

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [contextReady, setContextReady] = useState(false);
  const [patientGoal, setPatientGoal] = useState(defaultPatientGoal);
  const autoStartedRef = useRef(false);

  const isLocaleRoot = routing.locales.some((supportedLocale) => {
    const normalizedPath = String(pathname || '');
    return normalizedPath === `/${supportedLocale}` || normalizedPath === `/${supportedLocale}/`;
  });
  const isDashboardRoute = String(pathname || '').includes('/dashboard');
  const hideGuideOnAuthRoutes = /\/(register|login|verify|recovery|update-password|auth)\b/.test(
    String(pathname || '')
  );
  // Landing first seconds: only one clear path — hide floating guide until dashboard.
  const hideGuideOnLanding = isLocaleRoot;
  const hideFloatingGuide = hideGuideOnAuthRoutes || hideGuideOnLanding;

  function resolveGoalByContext() {
    if (isLocaleRoot && isAuthenticated === false) {
      return LANDING_VISITOR_GOAL;
    }

    if (isDashboardRoute) {
      return patientGoal || defaultPatientGoal;
    }

    return defaultPatientGoal;
  }

  useEffect(() => {
    let cancelled = false;

    async function loadAuthAndGoalContext() {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (cancelled) return;
        setIsAuthenticated(Boolean(user));

        if (!user || !isDashboardRoute) {
          setContextReady(true);
          return;
        }

        const { data: activeGoal } = await supabase
          .from('user_goals')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (cancelled || !activeGoal) {
          setContextReady(true);
          return;
        }

        const motivation = String(activeGoal.motivation_text || '').trim();
        const goalType = String(activeGoal.goal_type || '').trim();
        const targetWeight = activeGoal.target_weight_kg ?? activeGoal.target_weight;

        const contextualGoal = motivation
          ? motivation
          : [
              goalType ? `goal_type:${goalType}` : '',
              targetWeight ? `target_weight_kg:${targetWeight}` : '',
            ]
              .filter(Boolean)
              .join(', ') || defaultPatientGoal;

        setPatientGoal(contextualGoal);
        setContextReady(true);
      } catch (error) {
        console.error('Error resolving onboarding context:', error);
        setContextReady(true);
      }
    }

    loadAuthAndGoalContext();

    return () => {
      cancelled = true;
    };
  }, [defaultPatientGoal, isDashboardRoute, supabase]);

  function ensureCurrencySelectorStep(rawSteps) {
    const safeSteps = Array.isArray(rawSteps) ? rawSteps : [];
    const hasCurrencyStep = safeSteps.some((step) => step?.target === '[data-tour="currency-selector"]');

    if (hasCurrencyStep) return safeSteps;

    const currencyStep = {
      id: 'currency-selector',
      target: '[data-tour="currency-selector"]',
      title: t('steps.1.title'),
      content: t('steps.1.content'),
      placement: 'bottom',
    };

    return [...safeSteps, currencyStep];
  }

  async function loadAndOpenTour(goal, source = 'manual') {
    setLoading(true);
    try {
      const path = await getOnboardingPath(goal, locale);

      const safeSteps = ensureCurrencySelectorStep(path?.steps);
      setSteps(safeSteps);
      setIsOpen(safeSteps.length > 0);

      if (safeSteps.length > 0 && source === 'auto') {
        localStorage.setItem(TOUR_SEEN_KEY, '1');
      }
    } catch (error) {
      console.error('Failed to load onboarding path', error);
      setSteps([]);
      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleGuide() {
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    if (steps.length > 0) {
      setIsOpen(true);
      return;
    }

    await loadAndOpenTour(resolveGoalByContext(), 'manual');
  }

  useEffect(() => {
    if (autoStartedRef.current) return;
    if (!contextReady) return;
    if (isAuthenticated === null) return;

    // First-contact retention: never auto-open tour for anonymous visitors on landing.
    if (!isAuthenticated) return;

    if (!isLocaleRoot && !isDashboardRoute) return;

    const wasSeen = localStorage.getItem(TOUR_SEEN_KEY) === '1';
    if (wasSeen) return;

    autoStartedRef.current = true;
    void loadAndOpenTour(resolveGoalByContext(), 'auto');
  }, [contextReady, isAuthenticated, isLocaleRoot, isDashboardRoute, patientGoal, locale]);

  function handleCloseTour() {
    setIsOpen(false);
    localStorage.setItem(TOUR_SEEN_KEY, '1');
  }

  return (
    <>
      {!hideFloatingGuide && (
        <div className="fixed bottom-5 right-5 z-[170]">
          <GuideModeToggleButton
            onClick={handleToggleGuide}
            active={isOpen}
            inactiveLabel={t('guideButton')}
            activeLabel={t('close')}
            label={loading ? t('loading') : undefined}
          />
        </div>
      )}

      <OnboardingTour
        steps={steps}
        isOpen={isOpen}
        onClose={handleCloseTour}
        labels={labels}
      />
    </>
  );
}



